const ANALYTICS_CONSENT_KEY = "harness.analytics-consent";
const clientEnv = import.meta.env ?? {};
const observabilityEnabled = clientEnv.VITE_OBSERVABILITY_ENABLED === "true";
const analyticsEnabled = clientEnv.VITE_ANALYTICS_ENABLED === "true";
let googleAnalyticsLoaded = false;
let clarityLoaded = false;
let sentryInitialization: Promise<void> | null = null;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
};

function hasAnalyticsConsent() {
  return window.localStorage.getItem(ANALYTICS_CONSENT_KEY) === "granted";
}

function loadScript(src: string, id: string) {
  return new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load analytics script: ${src}`));
    document.head.appendChild(script);
  });
}

async function initializeGoogleAnalytics() {
  const measurementId = clientEnv.VITE_GA_MEASUREMENT_ID;
  if (!analyticsEnabled || !measurementId || googleAnalyticsLoaded || !hasAnalyticsConsent())
    return;
  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.gtag =
    analyticsWindow.gtag ||
    ((...args: unknown[]) => {
      analyticsWindow.dataLayer?.push(args);
    });
  analyticsWindow.gtag("js", new Date());
  analyticsWindow.gtag("config", measurementId, { send_page_view: false });
  await loadScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
    "harness-google-analytics",
  );
  googleAnalyticsLoaded = true;
}

async function initializeClarity() {
  const projectId = clientEnv.VITE_CLARITY_PROJECT_ID;
  if (!analyticsEnabled || !projectId || clarityLoaded || !hasAnalyticsConsent()) return;
  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.clarity =
    analyticsWindow.clarity ||
    Object.assign(
      (...args: unknown[]) => {
        analyticsWindow.clarity?.q?.push(args);
      },
      { q: [] },
    );
  analyticsWindow.clarity("start", projectId);
  await loadScript(
    `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`,
    "harness-microsoft-clarity",
  );
  clarityLoaded = true;
}

async function initializeConsentGatedAnalytics() {
  await Promise.allSettled([initializeGoogleAnalytics(), initializeClarity()]);
}

export async function initializeObservability(router: unknown) {
  const sentryDsn = clientEnv.VITE_SENTRY_DSN;
  if (observabilityEnabled && sentryDsn) {
    sentryInitialization ??= (async () => {
      const Sentry = await import("@sentry/react");
      Sentry.init({
        dsn: sentryDsn,
        environment: clientEnv.VITE_SENTRY_ENVIRONMENT || "production",
        integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
        tracesSampleRate: Number(clientEnv.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1"),
      });
    })();
    await sentryInitialization;
  }

  await initializeConsentGatedAnalytics();
}

export function setAnalyticsConsent(granted: boolean) {
  window.localStorage.setItem(ANALYTICS_CONSENT_KEY, granted ? "granted" : "denied");
  if (granted) void initializeConsentGatedAnalytics();
}

export function captureAuthEvent(
  event:
    | "auth_sign_in_requested"
    | "auth_sign_in_completed"
    | "auth_sign_in_failed"
    | "auth_invite_detected"
    | "auth_invite_previewed"
    | "auth_invite_accepted"
    | "auth_invite_claimed"
    | "auth_invite_failed",
  properties: {
    method?: "email" | "google";
    invitePresent?: boolean;
    inviteKind?: "connection" | "claim-chart";
    stage?: "sign_in" | "preview" | "accept" | "claim";
  } = {},
) {
  const analyticsWindow = window as AnalyticsWindow;
  if (googleAnalyticsLoaded) analyticsWindow.gtag?.("event", event, properties);
  if (clarityLoaded) analyticsWindow.clarity?.("event", event);
}
